import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Chip, Spinner, Surface, Typography } from '@heroui/react';
import { Icon } from '@iconify/react';
import type { RosterUser } from '@/api/models';
import { getNodeTestProps } from '@/utils';
import { SHIFT_SCOPE } from '@/components/shortcuts';
import { PinPad } from '@/components/pinPad/PinPad';
import { usePinPhysicalKeys } from '@/components/pinPad/usePinPhysicalKeys';
import type { PinErrorMessage } from '@/components/pinPad/usePinAttemptError';
import {
    getInitials,
    getRoleLabelKey,
    ROLE_META,
} from '@/pages/sellerLogin/sellerLoginMeta';
import { useFocusOnArrival } from './useFocusOnArrival';

type ShiftApproverPinProps = {
    approver: RosterUser;
    approverPin: string;
    onPinDigit: (digit: string) => void;
    onPinBackspace: () => void;
    onPinEscape: () => void;
    approved: boolean;
    approvalErrorMessage: PinErrorMessage | null;
    /** The server asked us to wait: the pad takes no input until it ends. */
    isApprovalHeld: boolean;
    isVerifying: boolean;
};

/**
 * The co-sign's PIN sub-view (render-only): the chosen approver's identity
 * card, then the shared kiosk `PinPad`. A lockout, a rate limit (counted down,
 * the pad held meanwhile) and a wrong PIN each show their own localized
 * message. Verification runs against the real device-gated `/auth/pin/verify`
 * without swapping the seller's session.
 *
 * Keyboard (bos-seller#61): `usePinPhysicalKeys` feeds the pad from the
 * physical digits and `Backspace` while the shift dialog's scope is live, and
 * hands `Esc` to `onPinEscape` (clear, else back to the picker) — the dialog
 * keeps its own dismiss-on-Escape off while this sub-view shows. The sub-view
 * takes focus when it appears, since the picker tile that had it is gone, so
 * focus stays inside the dialog through every attempt. It shows the
 * browser's own focus-visible ring rather than hiding it (SEL-49).
 */
export const ShiftApproverPin: React.FC<ShiftApproverPinProps> = ({
    approver,
    approverPin,
    onPinDigit,
    onPinBackspace,
    onPinEscape,
    approved,
    approvalErrorMessage,
    isApprovalHeld,
    isVerifying,
}) => {
    const { t } = useTranslation('pages');
    const isPadDisabled = approved || isVerifying || isApprovalHeld;

    const flashedKey = usePinPhysicalKeys({
        isDisabled: isPadDisabled,
        onBackspace: onPinBackspace,
        onDigit: onPinDigit,
        onEscape: onPinEscape,
        scope: SHIFT_SCOPE,
    });
    const containerRef = useFocusOnArrival<HTMLDivElement>();

    const errorMessage = approvalErrorMessage
        ? t(approvalErrorMessage.key, approvalErrorMessage.values)
        : null;

    return (
        <div
            aria-label={t('seller.shift.approverPinPrompt')}
            className="flex flex-col items-center gap-5"
            ref={containerRef}
            role="group"
            tabIndex={-1}
            {...getNodeTestProps('seller-shift-approver-pin')}
        >
            <Surface
                className="flex w-full flex-col items-center gap-2 rounded-2xl p-4 text-center"
                variant="secondary"
            >
                <Avatar size="lg">
                    <Avatar.Fallback>
                        {getInitials(approver.name)}
                    </Avatar.Fallback>
                </Avatar>
                <div className="flex flex-col items-center gap-1">
                    <Typography type="h4" weight="bold">
                        {approver.name}
                    </Typography>
                    <Chip
                        color={ROLE_META[approver.role].color}
                        size="sm"
                        variant="soft"
                    >
                        <Icon
                            className="size-3.5"
                            icon={ROLE_META[approver.role].icon}
                        />
                        {t(getRoleLabelKey(approver.role))}
                    </Chip>
                    <Typography color="muted" type="body-sm">
                        {t('seller.shift.approverPinPrompt')}
                    </Typography>
                </div>
            </Surface>

            <div className="flex min-h-5 items-center justify-center text-center">
                {errorMessage ? (
                    <Typography
                        className="text-danger"
                        {...getNodeTestProps('seller-shift-pin-error')}
                        type="body-sm"
                    >
                        {errorMessage}
                    </Typography>
                ) : approved ? (
                    <Typography
                        className="text-success"
                        {...getNodeTestProps('seller-shift-approved')}
                        type="body-sm"
                    >
                        {t('seller.shift.approved')}
                    </Typography>
                ) : isVerifying ? (
                    <Spinner
                        aria-label={t('seller.shift.approverPinPrompt')}
                        size="sm"
                    />
                ) : null}
            </div>

            <PinPad
                flashedKey={flashedKey}
                hasError={!!errorMessage}
                isDisabled={isPadDisabled}
                onBackspace={onPinBackspace}
                onDigit={onPinDigit}
                pin={approverPin}
            />
        </div>
    );
};
